import React, { useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Check, Close, Delete } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from "@mui/material";

import { GenericOverview } from "../components/core/GenericOverview";
import { UserEdit } from "../components/User/UserEdit";
import { getOwnUserId } from "../components/core/helper";
import { deleteFetch } from "../hooks/useFetch";
import { IUser } from "../interfaces/IUser";

const getEmptyUser = () => {
    return {
        id: -1,
        username: "",
        first_name: "",
        last_name: "",
        is_superuser: true,
        is_active: true,
    } as IUser;
};

const formatDate = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleString("de-DE") : "–";

export const Users: React.FC = () => {
    const ownUserId = getOwnUserId();
    const [editUser, setEditUser] = useState(getEmptyUser());
    const [currentArray, setCurrentArray] = useState<IUser[]>([]);
    const [userToDelete, setUserToDelete] = useState<IUser | null>(null);

    const deleteWasSuccessfully = (wasSuccessfully: boolean) => {
        if (wasSuccessfully && userToDelete !== null) {
            setCurrentArray(currentArray.filter(x => x.id !== userToDelete.id));
        }
        setUserToDelete(null);
    };

    const columns: GridColDef[] = [
        { field: "id", headerName: "Nr.", width: 80 },
        { field: "username", headerName: "Benutzer", flex: 1 },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            valueGetter: (value, row: IUser) => `${row.first_name} ${row.last_name}`.trim(),
        },
        {
            field: "is_superuser",
            headerName: "Admin",
            width: 80,
            renderCell: (params: GridRenderCellParams<any, boolean>) =>
                params.value ? <Check /> : <Close />,
        },
        {
            field: "is_active",
            headerName: "Aktiv",
            width: 80,
            renderCell: (params: GridRenderCellParams<any, boolean>) =>
                params.value ? <Check /> : <Close />,
        },
        {
            field: "last_login",
            headerName: "Letzte Anmeldung",
            width: 170,
            valueGetter: (value) => formatDate(value),
        },
        {
            field: "date_joined",
            headerName: "Angelegt",
            width: 170,
            valueGetter: (value) => formatDate(value),
        },
        {
            field: "actions",
            headerName: "Aktionen",
            width: 80,
            renderCell: (params: GridRenderCellParams<IUser>) => (
                <Tooltip title={params.row.id === ownUserId ? "Eigenes Konto" : "Löschen"}>
                    <span>
                        <IconButton
                            disabled={params.row.id === ownUserId}
                            onClick={(event) => {
                                event.stopPropagation();
                                setUserToDelete(params.row);
                            }}
                        >
                            <Delete />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
    ];

    return (
        <GenericOverview
            title="Benutzer"
            btnTitle="Neuer Benutzer"
            columns={columns}
            editObject={editUser}
            setEditObject={setEditUser}
            currentArray={currentArray}
            setCurrentArray={setCurrentArray}
            fetchUrl="/user/"
            getEmptyObject={getEmptyUser}
            enableDialog
            dialogWidth="md"
        >
            <>
                {userToDelete !== null && (
                    <Dialog open onClose={() => setUserToDelete(null)}>
                        <DialogTitle>Löschen bestätigen</DialogTitle>
                        <DialogContent>
                            Soll der Benutzer „{userToDelete.username}“ gelöscht werden?
                            Alternativ lässt er sich über „Aktiv“ sperren.
                        </DialogContent>
                        <DialogActions>
                            <Button variant="outlined" onClick={() => setUserToDelete(null)}>
                                Abbruch
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => deleteFetch("user", userToDelete.id, deleteWasSuccessfully)}
                            >
                                Löschen
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
                <UserEdit
                    key={`user-${editUser.id}`}
                    userEdit={editUser}
                    setUserEdit={setEditUser}
                />
            </>
        </GenericOverview>
    );
};
